import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';

import { AppBar } from '@agilemd/ui-app-bar';
import { Button } from '@agilemd/ui-button-legacy';
import { Icon } from '@agilemd/ui-icon';
import { InlineTextEditor } from '@agilemd/ui-inline-text-editor';
import { addVariants, browserBack } from '@agilemd/ui-utils';

import styles from './styles.module.scss';

export interface FileBannerProps {
  /**
   * The url of the module's art
   */
  moduleArt?: string | null;
  /**
   * The file's name
   */
  fileTitle: string;
  /**
   * The file's subtitle
   */
  fileSubtitle?: string;
  /**
   * If provided, will allow editing of the fileTitle provided
   */
  onChangeFileTitle?: ((title: string) => void) | null;
  /**
   * If provided, will allow editing of the fileSubtitle provided
   */
  onChangeFileSubtitle?: ((title: string) => void) | null;
  /**
   * Slot to render into the right side of the banner
   */
  right?: React.ReactNode;
  /**
   * Slot to render into the left side of the banner (after the file (sub)title)
   */
  left?: React.ReactNode;
  /**
   * URL to go to when the back button is clicked
   */
  backButtonHref?: string;
  /**
   * Handler to call when the back button is clicked (overrides backButtonHref)
   */
  backButtonOnClick?: () => void;
  /**
   * Hide the `BACK` text in the button
   */
  hideBackButtonText?: boolean;
}

const FileBanner: React.FC<FileBannerProps> = ({
  fileTitle,
  fileSubtitle,
  onChangeFileTitle = null,
  onChangeFileSubtitle = null,
  moduleArt = null,
  right = null,
  left = null,
  backButtonHref = undefined,
  backButtonOnClick = undefined,
  hideBackButtonText = false
}: FileBannerProps) => {
  const hasBackButton = Boolean(backButtonHref || backButtonOnClick);
  return (
    <AppBar
      className={addVariants('file-banner', styles)}
      variant={['thin', 'light']}
      right={right}
      left={
        <>
          {hasBackButton && (
            <Button
              variant="flat"
              color="transparent"
              className={styles['file-banner__back']}
              href={backButtonHref}
              onClick={() => {
                if (backButtonOnClick) {
                  backButtonOnClick();
                  return;
                }

                browserBack(backButtonHref);
              }}
            >
              <Icon icon={faChevronLeft} className={styles['file-banner__back__icon']} />
              {!hideBackButtonText && (
                <span className={styles['file-banner__back__label']}>BACK</span>
              )}
            </Button>
          )}
          {moduleArt && <img src={moduleArt} alt="module art" className={styles['module-art']} />}
          <div className={addVariants('titles', styles)}>
            <div>
              <InlineTextEditor
                value={fileTitle}
                onChange={onChangeFileTitle ?? undefined}
                className={addVariants('title', styles)}
                isEditing={Boolean(onChangeFileTitle)}
                minWidth={100}
              />
            </div>
            <div>
              <InlineTextEditor
                value={fileSubtitle}
                onChange={onChangeFileSubtitle ?? undefined}
                className={addVariants('subtitle', styles)}
                isEditing={Boolean(onChangeFileSubtitle)}
                placeholder="Subtitle..."
                minWidth={100}
              />
            </div>
          </div>
          {left}
        </>
      }
    />
  );
};

export default FileBanner;
